// src/components/pos/CheckoutConfirmDialog.jsx
import React from 'react';
import { X, ShoppingCart, DollarSign } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const CheckoutConfirmDialog = ({ 
  cartItems, 
  paymentMethod, 
  onConfirm, 
  onCancel 
}) => {
  const { language } = useApp();
  const t = translations[language];

  // 获取商品显示名称
  const getDisplayName = (item) => {
    switch (language) {
      case 'en':
        return item.product_name_en || item.product_name;
      case 'km':
        return item.product_name_km || item.product_name;
      default:
        return item.product_name;
    }
  };

  // 计算总额
  const totalAmount = cartItems.reduce((sum, item) => 
    sum + (item.unit_price * item.quantity), 0
  );

  // 计算商品总数
  const totalItems = cartItems.reduce((sum, item) => 
    sum + item.quantity, 0
  );

  // 支付方式显示
  const getPaymentMethodText = () => {
    switch (paymentMethod) {
      case 'cash':
        return language === 'en' ? 'Cash' : language === 'km' ? 'សាច់ប្រាក់' : '现金';
      case 'card':
        return language === 'en' ? 'Card' : language === 'km' ? 'កាត' : '刷卡';
      case 'mobile':
        return language === 'en' ? 'Mobile Payment' : language === 'km' ? 'ទូរសព្ទ' : '移动支付';
      default:
        return paymentMethod;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <ShoppingCart className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">
              {language === 'en' ? 'Confirm Order' :
               language === 'km' ? 'បញ្ជាក់ការបញ្ជាទិញ' :
               '确认订单'}
            </h3>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 商品列表 - 可滚动 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {cartItems.map((item, index) => (
            <div 
              key={item.product_id || index}
              className="flex justify-between items-start p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium text-gray-800 text-sm">
                  {getDisplayName(item)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ${item.unit_price.toFixed(2)} × {item.quantity}
                </p>
              </div>
              <div className="text-right ml-4">
                <p className="font-bold text-blue-600">
                  ${(item.unit_price * item.quantity).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 底部汇总 */}
        <div className="border-t p-6 space-y-4 bg-gray-50">
          {/* 统计信息 */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {language === 'en' ? 'Total Items' :
                 language === 'km' ? 'ចំនួនទំនិញសរុប' :
                 '商品总数'}:
              </span>
              <span className="font-medium">{totalItems}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {language === 'en' ? 'Payment Method' :
                 language === 'km' ? 'វិធីបង់ប្រាក់' :
                 '支付方式'}:
              </span>
              <span className="font-medium">{getPaymentMethodText()}</span>
            </div>
          </div>

          {/* 总金额 */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-5 h-5 text-gray-600" />
              <span className="text-lg font-bold text-gray-800">
                {language === 'en' ? 'Total Amount' :
                 language === 'km' ? 'សរុប' :
                 '总金额'}:
              </span>
            </div>
            <span className="text-2xl font-bold text-blue-600">
              ${totalAmount.toFixed(2)}
            </span>
          </div>

          {/* 按钮组 */}
          <div className="flex space-x-3 pt-2">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              {language === 'en' ? 'Cancel' :
               language === 'km' ? 'បោះបង់' :
               '取消'}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
            >
              {language === 'en' ? 'Confirm Payment' :
               language === 'km' ? 'បញ្ជាក់ការទូទាត់' :
               '确认支付'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutConfirmDialog;